import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../services/api';

interface ShippingZone {
  id: string;
  name: string;
  code: string;
  city: string;
  province: string;
  shippingFee: number;
  estimatedDeliveryDays: number;
  isActive: boolean;
}

const emptyForm = {
  name: '',
  code: '',
  city: '',
  province: '',
  shippingFee: 10,
  estimatedDeliveryDays: 2,
  isActive: true,
};

const ShippingZones: React.FC = () => {
  const { t } = useTranslation('shipping');
  const [zones, setZones] = useState<ShippingZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingZone, setEditingZone] = useState<ShippingZone | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [expandedProvinces, setExpandedProvinces] = useState<Set<string>>(new Set());

  const fetchShippingZones = async () => {
    try {
      setLoading(true);
      const response = await api.get('/shipping-zones');
      const data: ShippingZone[] = response.data.data || [];
      setZones(data);
      // Auto-expand all provinces on first load
      const provinces = new Set<string>(data.map((z) => z.province).filter(Boolean) as string[]);
      setExpandedProvinces(provinces);
      setError('');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || t('messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShippingZones();
  }, []);

  // Group zones by province → city
  const grouped = useMemo(() => {
    const map = new Map<string, Map<string, ShippingZone[]>>();
    for (const zone of zones) {
      const prov = zone.province || 'Unknown';
      const city = zone.city || 'Unknown';
      if (!map.has(prov)) map.set(prov, new Map());
      const cityMap = map.get(prov)!;
      if (!cityMap.has(city)) cityMap.set(city, []);
      cityMap.get(city)!.push(zone);
    }
    return map;
  }, [zones]);

  const toggleProvince = (province: string) => {
    setExpandedProvinces((prev) => {
      const next = new Set(prev);
      if (next.has(province)) next.delete(province);
      else next.add(province);
      return next;
    });
  };

  // Unique province/city suggestions from existing zones for autocomplete
  const existingProvinces = useMemo(() => Array.from(new Set(zones.map((z) => z.province).filter(Boolean))).sort(), [zones]);
  const existingCities = useMemo(() => {
    if (!formData.province) return Array.from(new Set(zones.map((z) => z.city).filter(Boolean))).sort();
    return Array.from(new Set(zones.filter((z) => z.province === formData.province).map((z) => z.city).filter(Boolean))).sort();
  }, [zones, formData.province]);

  const openCreate = () => {
    setEditingZone(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (zone: ShippingZone) => {
    setEditingZone(zone);
    setFormData({
      name: zone.name,
      code: zone.code,
      city: zone.city || '',
      province: zone.province || '',
      shippingFee: zone.shippingFee,
      estimatedDeliveryDays: zone.estimatedDeliveryDays,
      isActive: zone.isActive,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        ...(formData.code.trim() ? { code: formData.code.trim() } : {}),
        city: formData.city.trim(),
        province: formData.province.trim(),
        shippingFee: formData.shippingFee,
        estimatedDeliveryDays: formData.estimatedDeliveryDays,
        isActive: formData.isActive,
      };

      if (editingZone) {
        await api.put(`/shipping-zones/${editingZone.id}`, payload);
      } else {
        await api.post('/shipping-zones', payload);
      }

      await fetchShippingZones();
      setShowModal(false);
      setEditingZone(null);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || t('messages.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (zone: ShippingZone) => {
    if (!window.confirm(t('messages.confirmDelete'))) return;
    try {
      await api.delete(`/shipping-zones/${zone.id}`);
      await fetchShippingZones();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || t('messages.deleteFailed'));
    }
  };

  const toggleActive = async (zone: ShippingZone) => {
    try {
      await api.put(`/shipping-zones/${zone.id}`, { isActive: !zone.isActive });
      await fetchShippingZones();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || t('messages.toggleFailed'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('title')}</h1>
          <p className="mt-2 text-sm text-gray-600">{t('subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <Plus size={18} />
          {t('addNeighborhood')}
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} className="absolute right-3 top-3 text-xl">&times;</button>
        </div>
      )}

      {zones.length === 0 ? (
        <div className="bg-white shadow-md rounded-lg px-6 py-12 text-center text-gray-500">
          {t('messages.noNeighborhoods')}
        </div>
      ) : (
        <div className="space-y-4">
          {Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([province, cityMap]) => (
            <div key={province} className="bg-white shadow-md rounded-lg overflow-hidden">
              {/* Province header */}
              <button
                type="button"
                onClick={() => toggleProvince(province)}
                className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedProvinces.has(province) ? (
                    <ChevronDown size={18} className="text-gray-500" />
                  ) : (
                    <ChevronRight size={18} className="text-gray-500" />
                  )}
                  <span className="font-semibold text-gray-800 text-base">{province}</span>
                  <span className="text-xs bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5 font-medium">
                    {Array.from(cityMap.values()).flat().length} {t('table.neighborhoods')}
                  </span>
                </div>
              </button>

              {expandedProvinces.has(province) && (
                <div className="divide-y divide-gray-100">
                  {Array.from(cityMap.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([city, cityZones]) => (
                    <div key={city}>
                      {/* City sub-header */}
                      <div className="px-6 py-2 bg-gray-50/60 flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{city}</span>
                        <span className="text-xs text-gray-400">({cityZones.length})</span>
                      </div>

                      {/* Zone rows */}
                      <table className="min-w-full">
                        <tbody className="divide-y divide-gray-100">
                          {cityZones.map((zone) => (
                            <tr key={zone.id} className={!zone.isActive ? 'bg-gray-50/50' : ''}>
                              <td className="px-6 py-3 text-sm font-medium text-gray-900 w-1/3">
                                {zone.name}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 font-mono">{zone.code}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-700">
                                ${zone.shippingFee.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {zone.estimatedDeliveryDays}d
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  zone.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {zone.isActive
                                    ? t('status.active', { ns: 'common', defaultValue: 'Active' })
                                    : t('status.inactive', { ns: 'common', defaultValue: 'Inactive' })}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm font-medium space-x-3 whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() => openEdit(zone)}
                                  className="text-indigo-600 hover:text-indigo-900 inline-flex items-center gap-1"
                                >
                                  <Pencil size={13} />
                                  {t('buttons.edit', { ns: 'common', defaultValue: 'Edit' })}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => toggleActive(zone)}
                                  className={zone.isActive ? 'text-amber-600' : 'text-green-600'}
                                >
                                  {zone.isActive ? t('actions.disable') : t('actions.enable')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(zone)}
                                  className="text-red-600 hover:text-red-900 inline-flex items-center gap-1"
                                >
                                  <Trash2 size={13} />
                                  {t('buttons.delete', { ns: 'common', defaultValue: 'Delete' })}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-gray-500 opacity-75" aria-hidden />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
              <h3 className="text-lg font-semibold mb-4">
                {editingZone ? t('editNeighborhood') : t('addNeighborhood')}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">

                {/* Province */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fields.province')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    list="province-suggestions"
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value, city: '' })}
                    placeholder={t('fields.provinceHint')}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                  <datalist id="province-suggestions">
                    {existingProvinces.map((p) => <option key={p} value={p} />)}
                  </datalist>
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fields.city')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    list="city-suggestions"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder={t('fields.cityHint')}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                  <datalist id="city-suggestions">
                    {existingCities.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>

                {/* Neighborhood name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fields.neighborhoodName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fields.codeOptional')}
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder={t('fields.codeHint')}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                {/* Shipping fee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fields.shippingFee')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.shippingFee}
                    onChange={(e) => setFormData({ ...formData, shippingFee: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                {/* Delivery days */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fields.estimatedDeliveryDays')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.estimatedDeliveryDays}
                    onChange={(e) => setFormData({ ...formData, estimatedDeliveryDays: parseInt(e.target.value, 10) || 1 })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  {t('fields.active')}
                </label>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border rounded-md text-gray-700"
                  >
                    {t('buttons.cancel', { ns: 'common', defaultValue: 'Cancel' })}
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md disabled:opacity-50"
                  >
                    {saving
                      ? t('buttons.saving', { ns: 'common', defaultValue: 'Saving…' })
                      : t('buttons.save', { ns: 'common', defaultValue: 'Save' })}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingZones;
